import createReducer from '../../createReducer';
import {
  accessTokenSuccess,
  clearAssociateDishPaymentIndex,
  clearBranches,
  clearCustomerSession,
  displayCustomerDetailsSummary,
  displayModal,
  fetchBranches,
  fetchBranchesFailure,
  fetchBranchesSuccess,
  fullOrganizationError,
  fullOrganizationSuccess,
  hideCustomerDetailsSummary,
  hideModal,
  indicateFetchOrganization,
  popFromHistoryStack,
  pushToHistoryStack,
  setAssociateDishPaymentIndex,
  setFirstTime,
  setInstance,
  setMsid,
  setUser,
  sideMenuDisplay,
  toggleCustomerDetailsSummary,
  toggleModal,
} from '../actions/SessionActions';
import { getInternalMenuAndItemsHash } from '../../../utils/DishUtils';
import { pushItem, removeItem } from '../../../utils/reducerUtils';
import { startEditing } from '../actions/EditDishActions';
import { Modals } from '../../../utils/constants';
import { isManagerDiscount } from '../../../utils/ChargeUtils';
import _ from 'lodash';
import { isOrganizationOfflineCreditCard } from '../../../utils/CheckoutUtils';
import { getEmptyState } from '../../../utils/emptyStateUtils';
import { parseInstance } from '../../../utils/instanceUtils';
import { isUsingCashier } from '../../../utils/CashierUtils';

export const LOCAL_STORAGE_FIRST_TIME_KEY = 'WixPhoneOrdering';

export const FLOW_STEP = {
  INIT: 'INIT', // Welcome screen
  IDLE: 'IDLE', // Phone entry screen
  CUSTOMER_DETAILS: 'CUSTOMER_DETAILS',
  ADDRESS: 'ADDRESS',
  SELECT_BRANCH: 'SELECT_BRANCH',
  ORDER: 'ORDER',
  EDIT_DISH: 'EDIT_DISH',
  CHECKOUT: 'CHECKOUT',
  FIRST_TIME: 'FIRST_TIME',
};

const initialState = {
  step: FLOW_STEP.INIT,
  stepData: null,
  acceptsTip: false,
  allowsManagerDiscount: false,
  user: [],
  msid: '',
  accessToken: null,
  localeData: {},
  organization: {},
  menu: {},
  customer: {},
  error: null,
  lowResMenuShown: false,
  branches: [],
  loadingBranches: false,
  loadingOrganization: false,
  displayCustomerDetailsSummary: false,
  modals: [],
  allowSplitPayments: false,
  historyStack: [FLOW_STEP.INIT],
  isFirstTime: false,
  instance: {},
  fallbackInstance: {},
};

const newCustomerState = {
  step: FLOW_STEP.IDLE,
  stepData: undefined,
  acceptsTip: false,
  customer: {},
  error: null,
  lowResMenuShown: false,
  branches: [],
  loadingBranches: false,
  loadingOrganization: false,
  displayCustomerDetailsSummary: false,
  modals: [],
  allowSplitPayments: false,
  historyStack: [FLOW_STEP.INIT, FLOW_STEP.IDLE],
  emptyState: null,
};

function getAcceptsTip(charges) {
  if (!charges) {
    return false;
  }
  const tip = charges.find(charge => charge.type === 'tip' && charge.state === 'operational');
  return Boolean(tip);
}

function getAllowsManagerDiscount(charges) {
  if (!charges) {
    return false;
  }
  return charges.filter(charge => isManagerDiscount(charge)).length > 0;
}

function displayModalImpl(state, modal) {
  const index = state.modals.indexOf(modal);
  if (index === -1) {
    return {
      ...state,
      modals: pushItem(state.modals, modal),
    };
  } else {
    return state;
  }
}

const handlers = {
  [setUser]: (state, payload) => {
    return {
      ...state,
      user: payload.user,
    };
  },
  [accessTokenSuccess]: (state, payload) => {
    return {
      ...state,
      accessToken: payload.accessToken,
    };
  },
  [fullOrganizationSuccess]: (state, payload) => {
    const acceptsTip =
      payload.full.menu &&
      getAcceptsTip(payload.full.menu.chargesV2) &&
      (!isOrganizationOfflineCreditCard(payload.full.restaurant) || isUsingCashier(payload.full.restaurant));

    const allowsManagerDiscount = payload.full.menu && getAllowsManagerDiscount(payload.full.menu.chargesV2);
    const organization = payload.full.restaurant;
    const internalMenuAndItemsHash = getInternalMenuAndItemsHash(payload.full.menu);

    return {
      ...state,
      organization,
      ...internalMenuAndItemsHash,
      loadingOrganization: false,
      acceptsTip,
      allowsManagerDiscount,
      emptyState: getEmptyState({ organization, simplifiedMenu: internalMenuAndItemsHash.simplifiedMenu }),
    };
  },
  [fullOrganizationError]: state => {
    return {
      ...state,
      loadingOrganization: false,
    };
  },
  [sideMenuDisplay]: (state, payload) => {
    return {
      ...state,
      lowResMenuShown: payload.value,
    };
  },
  [fetchBranches]: state => {
    return {
      ...state,
      loadingBranches: true,
    };
  },
  [fetchBranchesSuccess]: (state, payload) => {
    return {
      ...state,
      branches: payload.organizations,
      loadingBranches: false,
    };
  },
  [fetchBranchesFailure]: state => {
    return {
      ...state,
      branches: [],
      loadingBranches: false,
    };
  },
  [clearBranches]: state => {
    return {
      ...state,
      branches: [],
    };
  },
  [indicateFetchOrganization]: state => {
    return {
      ...state,
      loadingOrganization: true,
    };
  },
  [toggleCustomerDetailsSummary]: state => {
    return {
      ...state,
      displayCustomerDetailsSummary: !state.displayCustomerDetailsSummary,
    };
  },
  [displayCustomerDetailsSummary]: state => {
    return {
      ...state,
      displayCustomerDetailsSummary: true,
    };
  },
  [hideCustomerDetailsSummary]: state => {
    return {
      ...state,
      displayCustomerDetailsSummary: false,
    };
  },
  [setAssociateDishPaymentIndex]: (state, payload) => {
    const { associateDishPaymentIndex } = payload;

    return {
      ...state,
      associateDishPaymentIndex,
    };
  },
  [clearAssociateDishPaymentIndex]: state => {
    return {
      ...state,
      associateDishPaymentIndex: undefined,
    };
  },
  [displayModal]: (state, action) => {
    return displayModalImpl(state, action.modal);
  },
  [hideModal]: (state, action) => {
    const index = state.modals.indexOf(action.modal);
    return {
      ...state,
      modals: removeItem(state.modals, index),
    };
  },
  [toggleModal]: (state, action) => {
    const index = state.modals.indexOf(action.modal);
    const modals = index === -1 ? pushItem(state.modals, action.modal) : removeItem(state.modals, index);

    return {
      ...state,
      modals,
    };
  },
  [startEditing]: state => {
    return displayModalImpl(state, Modals.EDIT_DISH);
  },

  [clearCustomerSession]: (state, action) => {
    const step = action.step ? { step: action.step } : {};
    return {
      ...state,
      ...newCustomerState,
      ...step,
    };
  },
  [pushToHistoryStack]: (state, action) => {
    return {
      ...state,
      historyStack: pushItem(state.historyStack, action.step),
      step: action.step,
    };
  },
  [popFromHistoryStack]: state => {
    let historyStack = state.historyStack;

    if (_.isEmpty(state.modals)) {
      const newHistoryStack = removeItem(state.historyStack, state.historyStack.length - 1);
      historyStack = _.isEmpty(newHistoryStack) ? [FLOW_STEP.INIT] : newHistoryStack;
    }

    return {
      ...state,
      historyStack,
      step: _.last(historyStack),
      modals: [],
    };
  },

  [setFirstTime]: (state, payload) => {
    return {
      ...state,
      isFirstTime: payload.value,
    };
  },
  [setMsid]: (state, payload) => {
    return {
      ...state,
      msid: payload.msid,
    };
  },
  [setInstance]: (state, payload) => {
    const { signedInstance, fallbackSignedInstance } = payload;

    const instance = signedInstance ? parseInstance(signedInstance) : state.signedInstance;
    const fallbackInstance = fallbackSignedInstance
      ? parseInstance(fallbackSignedInstance)
      : state.fallbackSignedInstance;

    return {
      ...state,
      instance,
      fallbackInstance,
    };
  },
};

export default createReducer(initialState, handlers);
